import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { HideCaseDialog, HideCaseForm, HideCasePost } from '@core/models/leave-admin/hide-case.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Observable } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { ErrorService, LayoutService } from '@core/services';
import { SelectionOption, StiiraError } from '@core/models';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { finalize, takeUntil } from 'rxjs/operators';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';

@Component({
    selector: 'app-hide-case',
    templateUrl: './hide-case.component.html',
    styleUrls: ['./hide-case.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HideCaseComponent implements OnInit {
  static readonly componentName = 'HideCaseComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  public form: FormGroup<HideCaseForm>;
  public canClose: boolean;
  public isSubmitting$: Subject<boolean>;
  
  public position = position;
  public onDragEnded = onDragEnded;

  private formInitValues: any;
  private hideCasePost: HideCasePost;
  private destroy$: Subject<void> = new Subject<void>();

  public hideReasonOptions: SelectionOption[] = [];

  get noChanges(): boolean {
    return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
  }

  get hideReasonId(): FormControl {
    return this.form.controls.hideReasonId as FormControl;
  }

  get requiresComments(): boolean {
    return this.data.hcdata.hideReasons.find(hr => hr.hideReasonId == this.hideReasonId.value)?.requiresComments == true;
  }

  get comments(): FormControl {
    return this.form.controls.comments as FormControl;
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      caseId: number, 
      hcdata: HideCaseDialog; 
      sysText: any },
    private hideCaseDialogComponent: MatDialogRef<HideCaseComponent>,
    private service: LeaveAdminService,
    private store: LeaveAdminStoreService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private errorService: ErrorService, 
    private layoutService: LayoutService
  ) {
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group<HideCaseForm>({
      caseId: this.fb.control(this.data.caseId),
      hideReasonId: this.fb.control(null, Validators.required),
      comments: this.fb.control(null) })
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.hideCaseDialogComponent, this.isSubmitting$, () => this.cancel());

    this.formInitValues = { ...this.form.value };

    this.comments.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=> {
        if (!this.noChanges)
          this.isEditing.emit(true);
        });

    this.data.hcdata.hideReasons
      .forEach(hr => this.hideReasonOptions
        .push({ 
          id: hr.hideReasonId, 
          description: hr.name }
        ));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public hideCase(): void {
    if (this.store.isActionRunning) {
      this.openPendingChangesDialog();
      return;
    }

    this.hideCasePost = {
      caseId: this.data.caseId,
      hideReasonId: this.hideReasonId.value,
      comments: this.comments.value };

    this.service.postHideCase(this.hideCasePost)
    .pipe(
      indicate(this.isSubmitting$),
      finalize(
        () => { }))
    .subscribe(
      (res) => { 
        this.hideCaseDialogComponent.close(res);
      },
      (err: StiiraError) => {
        this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors);
      });
  }

  public cancel(): void {
    if (this.noChanges)
      this.hideCaseDialogComponent.close();
    else
      this.openUnsavedChangesDialog();
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.sysText.leavePage
    };

    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed()
      .subscribe(
        (res: boolean) => {
          if (res)
            this.hideCaseDialogComponent.close(); 
        });
  }

  public hideReasonChangeHandler (): void {
    this.isEditing.emit(true);

    if (this.requiresComments)
      this.comments.setValidators(Validators.required);
    else
      this.comments.removeValidators(Validators.required);

    this.form.updateValueAndValidity();
    this.form.markAsTouched();
  }

  private openPendingChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.hideCase();
      }
    });
  }
}